/**
 * @fileoverview Reactivate User form hook.
 * @description The Reactivate dialog's form logic: the role, manager and reason fields validated by
 * the schema with the configuration's note limit; the manager choices for the chosen role (the
 * shared eligible-reporting-managers query, also used by Add and Invite User); the manager
 * prefill; clearing the manager when the role changes (one is only valid for the role it was
 * picked for); the translated field errors; and the submit handler that builds the request body.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { OTHER_REASON_CODE } from '@/features/users/constants/lifecycle.constants'
import { useManagerPrefill } from '@/features/users/hooks/useManagerPrefill'
import {
  buildReactivateUserFormSchema,
  type ReactivateUserFormValues,
} from '@/features/users/schemas/reactivate-user-form.schema'
import { initialRoleId, toReactivateUserRequest } from '@/features/users/utils/reactivation.utils'
import { useEligibleReportingManagers } from '@/shared/hooks/useEligibleReportingManagers'

import type {
  ReactivateUserRequest,
  ReactivationRoleOption,
  UserDetail,
} from '@/features/users/types/users.types'
import type { FormEvent } from 'react'

interface ReactivateUserFormOptions {
  user: UserDetail
  roles: ReactivationRoleOption[]
  noteMaxLength: number
  onSubmit: (payload: ReactivateUserRequest) => void
}

/**
 * @description Runs the Reactivate form: its fields, manager choices, validation and submit.
 * @param {ReactivateUserFormOptions} options - The user coming back, the roles the admin may
 * choose, the note limit, and what to do with the request once the form is valid.
 * @returns {object} The form, the manager choices query, whether the note is required, the
 * translated field errors, the role change handler and the submit handler for the form element.
 */
export function useReactivateUserForm({
  user,
  roles,
  noteMaxLength,
  onSubmit: submitRequest,
}: ReactivateUserFormOptions) {
  const { t } = useTranslation()
  const form = useForm<ReactivateUserFormValues>({
    resolver: zodResolver(buildReactivateUserFormSchema({ noteMaxLength })),
    defaultValues: {
      roleId: initialRoleId(user, roles),
      reportingManagerUserId: '',
      reasonCode: '',
      reasonNote: '',
    },
  })
  const { errors } = form.formState
  const roleId = useWatch({ control: form.control, name: 'roleId' })
  const reasonCode = useWatch({ control: form.control, name: 'reasonCode' })
  const managersQuery = useEligibleReportingManagers(roleId, true)
  useManagerPrefill({
    form,
    roleId,
    managers: managersQuery.data,
    settled: managersQuery.isSuccess && !managersQuery.isFetching,
    previousManagerId: user.reportingManager?.userId,
  })

  /**
   * @description Translates a validation message key, if there is one.
   * @param {string | undefined} key - The i18n key from the schema, if the field is invalid.
   * @returns {string | undefined} The message to show.
   */
  const message = (key: string | undefined) => (key ? t(key) : undefined)

  /**
   * @description Clears the manager when the role changes: one is only valid for the role it was
   * picked for.
   */
  const onRoleChange = () => {
    form.setValue('reportingManagerUserId', '')
  }

  /**
   * @description Sends the chosen role, manager and reason.
   * @param {ReactivateUserFormValues} values - The validated values.
   */
  const onValid = (values: ReactivateUserFormValues) => {
    submitRequest(toReactivateUserRequest(values))
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void form.handleSubmit(onValid)(event)
  }

  return {
    form,
    managersQuery,
    noteRequired: reasonCode === OTHER_REASON_CODE,
    roleError: message(errors.roleId?.message),
    managerError: message(errors.reportingManagerUserId?.message),
    reasonError: message(errors.reasonCode?.message),
    noteError: message(errors.reasonNote?.message),
    onRoleChange,
    onSubmit,
  }
}
